import * as React from 'react'
import { canLoginAsUser, canDeleteApplication } from 'utils/roles'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Divider from '@mui/joy/Divider'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Table from '@mui/joy/Table'
import Sheet from '@mui/joy/Sheet'
import IconButton from '@mui/joy/IconButton'
import Typography from '@mui/joy/Typography'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import axios from 'axios'
import { ClientApplication, FACTORING_FEATURE_FLAGS, FeatureType, GluonEnvironment, IFeature, UserRole } from '@numeo/types-core'
import { getStoredEnvironment } from 'setup/SetupAxios'
import ApplicationDetails from './ApplicationDetails'
import { OpenInNew, Login as LoginIcon, MergeType as MergeTypeIcon } from '@mui/icons-material'
import {
    CircularProgress,
    Checkbox,
    List,
    ListItem,
    Menu,
    MenuItem,
    Dropdown,
    MenuButton,
    Tabs,
    TabList,
    Tab,
    Tooltip,
    Alert,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/joy'
import FlagIcon from '@mui/icons-material/Flag'

const REMOVED_ELD_FEATURE_FLAGS = new Set<FeatureType>([FeatureType.SwiftEld, FeatureType.WheelsElogEld])
// Per-provider factoring flags are no longer manually toggled: factoring shows to all
// accounting-enabled users, and each provider sets its own flag on connect. Hide them here.
const REMOVED_FACTORING_FEATURE_FLAGS = new Set<FeatureType>(FACTORING_FEATURE_FLAGS)
const FEATURE_FLAG_OPTIONS = (Object.values(FeatureType) as FeatureType[]).filter(
    (feature) => !REMOVED_ELD_FEATURE_FLAGS.has(feature) && !REMOVED_FACTORING_FEATURE_FLAGS.has(feature)
)

const FEATURE_LABELS: Partial<Record<FeatureType, string>> = {
    [FeatureType.FuelAI]: 'FuelAI (Admin)',
    [FeatureType.FuelAiUsageInsights]: 'Fuel AI Usage Insights (Owner)',
    [FeatureType.ReverseDispatch]: 'Reverse Dispatch (Telegram → Fuel AI; enables Auto Fuel toggle)',
    [FeatureType.FuelInDispatch]: 'Fuel Stops in Dispatch (manual attach)',
    [FeatureType.EditStopScheduledTimes]: 'Edit Stop Scheduled Times (Arrival/Departure)',
    [FeatureType.PerTabCompanySwitch]: 'Multi-MC Per-Tab Switch',
    [FeatureType.AutoArchiveFromLoadsList]: 'Auto-Archive from Ongoing Loads list (needs Updater → Auto Jobs → Archive Old Loads on)',
}

const getFeatureLabel = (feature: FeatureType) => FEATURE_LABELS[feature] || feature

const normalizeFeatureFlags = (features: IFeature[] | undefined): IFeature[] => {
    const map = new Map<string, boolean>()
    for (const feature of features || []) {
        if (!feature?.feature) continue
        map.set(String(feature.feature), Boolean(feature.enabled))
    }

    return Array.from(map.entries()).map(([feature, enabled]) => ({
        feature: feature as FeatureType,
        enabled,
    }))
}

// Resolve the application's owner user from the embedded users[] via the `owner`
// pointer (the source of truth for who controls the account). Falls back to the
// first ADMIN-role user, then null, so legacy/edge applications still render.
const getOwnerUser = (application: ClientApplication) =>
    application.users?.find((u) => u.id === String(application.owner)) ?? application.users?.find((u) => u.role === UserRole.ADMIN)

export default function Applications() {
    const [open, setOpen] = React.useState(false)
    const [applications, setApplications] = React.useState<ClientApplication[]>([])
    const [loading, setLoading] = React.useState(true)
    const [fetching, setFetching] = React.useState(false)
    const [loginAsError, setLoginAsError] = React.useState('')
    const [, setSelectedApplication] = React.useState<ClientApplication | null>(null)
    const [editedData, setEditedData] = React.useState<ClientApplication | null>(null)
    const [updating, setUpdating] = React.useState(false)
    const [drawerOpen, setDrawerOpen] = React.useState(false)
    const [searchQuery, setSearchQuery] = React.useState('')
    const [emailSearchQuery, setEmailSearchQuery] = React.useState('')
    const [nylasEmailSearchQuery, setNylasEmailSearchQuery] = React.useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('')
    const [debouncedEmailSearchQuery, setDebouncedEmailSearchQuery] = React.useState('')
    const [debouncedNylasEmailSearchQuery, setDebouncedNylasEmailSearchQuery] = React.useState('')
    const [statusFilter, setStatusFilter] = React.useState('all')
    const [integrationFilter, setIntegrationFilter] = React.useState('all')
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
    const [applicationToDelete, setApplicationToDelete] = React.useState<ClientApplication | null>(null)
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = React.useState(false)
    const [deleting, setDeleting] = React.useState(false)
    const [featureFlagLoading, setFeatureFlagLoading] = React.useState<{ [key: string]: boolean }>({})
    const [featureFlagSuccess, setFeatureFlagSuccess] = React.useState<{ [key: string]: boolean }>({})
    const [selectedFeatures, setSelectedFeatures] = React.useState<{ [key: string]: IFeature[] }>({})
    const [userTypeTab, setUserTypeTab] = React.useState('all')

    const [deletingProject, setDeletingProject] = React.useState('')
    const [deletingProjectInput, setDeletingProjectInput] = React.useState('')
    const [deleteSuccess, setDeleteSuccess] = React.useState('')
    const [featureFilter, setFeatureFilter] = React.useState('all')

    // Pagination state
    const [page, setPage] = React.useState(1)
    const [pageSize] = React.useState(20)
    const [totalCount, setTotalCount] = React.useState(0)
    const [totalPages, setTotalPages] = React.useState(0)

    const fetchApplications = async (isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setLoading(true)
            } else {
                setFetching(true)
            }

            // Build query params
            const params = new URLSearchParams()

            // Pagination
            params.append('page', page.toString())
            params.append('pageSize', pageSize.toString())

            // Filters (using debounced values)
            if (debouncedSearchQuery) params.append('projectName', debouncedSearchQuery)
            if (debouncedEmailSearchQuery) params.append('email', debouncedEmailSearchQuery)
            if (debouncedNylasEmailSearchQuery) params.append('nylasEmail', debouncedNylasEmailSearchQuery)
            if (statusFilter !== 'all') params.append('status', statusFilter)
            if (integrationFilter !== 'all') params.append('integration', integrationFilter)
            if (featureFilter !== 'all') params.append('feature', featureFilter)
            if (userTypeTab !== 'all') params.append('userType', userTypeTab)

            const response = await axios.get(`/gluon/applications?${params.toString()}`)

            if (response.data) {
                setApplications(response.data.applications || [])
                setTotalCount(response.data.total || 0)
                setTotalPages(response.data.totalPages || 0)
            } else {
                setApplications([])
                setTotalCount(0)
                setTotalPages(0)
            }
        } catch {
            setApplications([])
            setTotalCount(0)
            setTotalPages(0)
        } finally {
            setLoading(false)
            setFetching(false)
        }
    }

    // Debounce search queries (500ms delay)
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedEmailSearchQuery(emailSearchQuery)
        }, 500)
        return () => clearTimeout(timer)
    }, [emailSearchQuery])

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedNylasEmailSearchQuery(nylasEmailSearchQuery)
        }, 500)
        return () => clearTimeout(timer)
    }, [nylasEmailSearchQuery])

    // Initial load
    React.useEffect(() => {
        fetchApplications(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only initial fetch
    }, [])

    // Subsequent fetches when filters/page change (using debounced search values)
    React.useEffect(() => {
        if (!loading) {
            fetchApplications(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on filter/page changes only, not on loading or the recreated fetcher
    }, [page, debouncedSearchQuery, debouncedEmailSearchQuery, debouncedNylasEmailSearchQuery, statusFilter, integrationFilter, featureFilter, userTypeTab])

    // Reset to page 1 when filters change (using debounced search values)
    React.useEffect(() => {
        if (page !== 1) {
            setPage(1)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when a filter changes, not when the page does
    }, [debouncedSearchQuery, debouncedEmailSearchQuery, debouncedNylasEmailSearchQuery, statusFilter, integrationFilter, featureFilter, userTypeTab])

    const handleRowClick = (application: ClientApplication) => {
        setSelectedApplication(application)
        setEditedData(application)
        setDrawerOpen(true)
    }

    const handleCloseDrawer = () => {
        setDrawerOpen(false)
        setSelectedApplication(null)
        setEditedData(null)
    }

    const handleJsonChange = (edit: { updated_src: ClientApplication }) => {
        if (editedData) {
            setEditedData(edit.updated_src)
        }
    }

    const handleUpdate = async () => {
        if (!editedData) return

        setUpdating(true)
        try {
            const { id, ...editedFields } = editedData
            const updateData: Partial<ClientApplication> = { ...editedFields }
            delete updateData.createdAt
            delete updateData.updatedAt

            // Ensure updateData is a valid object
            if (typeof updateData !== 'object' || updateData === null) {
                throw new Error('Invalid update data')
            }

            const response = await axios.put(`/gluon/applications/${id}`, updateData)
            const updatedApplication = response.data

            setApplications((prevApplications) => prevApplications.map((app) => (app.id === id ? { ...app, ...updatedApplication } : app)))
            handleCloseDrawer()
        } catch (error) {
            console.error('Error updating application:', error)
        } finally {
            setUpdating(false)
        }
    }

    const handleDeleteClick = (e: React.MouseEvent, application: ClientApplication) => {
        e.stopPropagation()
        setApplicationToDelete(application)
        setDeleteModalOpen(true)
    }

    const handleDeleteConfirm = async () => {
        if (!applicationToDelete) return

        setDeleting(true)
        try {
            // Call the DELETE endpoint which handles both Nylas disconnection and application soft deletion
            await axios.post(`/gluon/applications/${applicationToDelete.id}`)

            // Update the applications list by setting status to inactive
            setApplications((prevApplications) =>
                prevApplications.map((app) =>
                    app.id === applicationToDelete.id
                        ? { ...app, status: 'inactive', deletedAt: new Date(), integrations: { nylas: { emails: [], enabled: false } } }
                        : app
                )
            )

            // Close the delete modal
            setDeleteModalOpen(false)
            setApplicationToDelete(null)
        } catch (error) {
            console.error('Error deleting application:', error)
        } finally {
            setDeleting(false)
        }
    }

    const handleHardDeleteClick = async (e: React.MouseEvent, projectName: string) => {
        e.stopPropagation()
        setDeletingProject(projectName)
        setDeleteConfirmModalOpen(true)
    }

    const handleHardDeleteConfirm = async () => {
        setDeleteConfirmModalOpen(false)
        try {
            await axios.delete(`/gluon/applications`, { data: { projectName: deletingProject } })
            setDeleteSuccess(deletingProject)
            setDeletingProject('')
            setDeletingProjectInput('')

            // Clear success message after 5 seconds
            setTimeout(() => {
                setDeleteSuccess('')
            }, 5000)

            fetchApplications()
        } catch (error) {
            console.error('Error deleting application:', error)
        }
    }

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false)
        setApplicationToDelete(null)
    }

    const handleFeatureFlagOpen = (application: ClientApplication) => {
        // Initialize selected features based on application's current features
        const appFeatures = normalizeFeatureFlags(application.features)
        setSelectedFeatures({
            ...selectedFeatures,
            [application.id]: appFeatures,
        })
    }

    const handleFeatureToggle = (appId: string, feature: FeatureType) => {
        setSelectedFeatures((prev) => {
            const currentFeatures = [...(prev[appId] || [])]
            const featureIndex = currentFeatures.findIndex((f) => f.feature === feature)

            if (featureIndex >= 0) {
                // Toggle existing feature
                currentFeatures[featureIndex] = {
                    ...currentFeatures[featureIndex],
                    enabled: !currentFeatures[featureIndex].enabled,
                }
            } else {
                // Add new feature
                currentFeatures.push({
                    feature,
                    enabled: true,
                })
            }

            return {
                ...prev,
                [appId]: currentFeatures,
            }
        })
    }

    const isFeatureEnabled = (appId: string, feature: FeatureType): boolean => {
        const features = selectedFeatures[appId] || []
        return features.some((f) => f.feature === feature && Boolean(f.enabled))
    }

    const saveFeatureFlags = async (e: React.MouseEvent, application: ClientApplication) => {
        // Prevent the dropdown from closing
        e.preventDefault()
        e.stopPropagation()

        try {
            // Reset success state
            setFeatureFlagSuccess((prev) => ({ ...prev, [application.id]: false }))
            setFeatureFlagLoading((prev) => ({ ...prev, [application.id]: true }))

            const features = normalizeFeatureFlags(selectedFeatures[application.id] || [])

            // Ensure all feature types are represented
            const allFeatures = FEATURE_FLAG_OPTIONS.map((featureType) => {
                const existingFeature = features.find((f) => f.feature === featureType)
                return {
                    feature: featureType,
                    enabled: Boolean(existingFeature?.enabled),
                }
            })

            const response = await axios.put(`/gluon/applications/features?projectName=${application.projectName}`, allFeatures)

            if (response.data.success) {
                // Update the application in the local state
                setApplications((prevApps) => prevApps.map((app) => (app.id === application.id ? { ...app, features: response.data.features } : app)))

                // Set success state
                setFeatureFlagSuccess((prev) => ({ ...prev, [application.id]: true }))

                try {
                    // Use postMessage to communicate with extension content script
                    window.postMessage({ type: 'NUMEO_SYNC_AUTH_DATA', source: 'control-panel' }, '*')
                } catch {
                    // Extension not available, continue normally
                }

                // Reset success state after 1 second
                setTimeout(() => {
                    setFeatureFlagSuccess((prev) => ({ ...prev, [application.id]: false }))
                }, 1000)
            }
        } catch (error) {
            console.error('Error saving feature flags:', error)
            setFeatureFlagSuccess((prev) => ({ ...prev, [application.id]: false }))
        } finally {
            setFeatureFlagLoading((prev) => ({ ...prev, [application.id]: false }))
        }
    }

    const handleEditClick = (application: ClientApplication) => {
        setSelectedApplication(application)
        setEditedData(application)
        setDrawerOpen(true)
    }

    // No longer need client-side filtering since backend handles it
    const filteredApplications = applications

    const onOpenRemoteJS = async (event: React.MouseEvent, projectName: string) => {
        event.stopPropagation()
        try {
            const channelId = `numeo-${projectName}-${Date.now()}`
            window.open(`https://remotejs.com/viewer/${channelId}`)
            setTimeout(async () => {
                await axios.post('/gluon/debug/enable-remote-js', {
                    channelId,
                    projectName,
                })
            }, 5000)
        } catch (error) {
            console.error(error)
        }
    }

    // Log into a customer's account as its owner. The gluon backend (operator-authorized) mints a
    // session token scoped to this application — no customer credential is read or passed — so this
    // works even for accounts with no login method of their own. The token is handed to the admin
    // panel of the SAME environment, which stores it and strips it from the URL.
    const handleLoginAsOwner = async (e: React.MouseEvent, application: ClientApplication) => {
        e.stopPropagation()
        setLoginAsError('')
        // Open the tab synchronously (inside the click gesture) so the popup blocker doesn't kill it
        // after the async token request resolves. Populated with the real URL once we have the token.
        const win = window.open('about:blank', '_blank')
        // Sever the opener link while the tab is still same-origin (about:blank), before navigating it
        // cross-origin — mirrors `noopener` and prevents the opened tab from reaching back into gluon.
        if (win) win.opener = null
        try {
            const { data } = await axios.post<{ token: string }>('/gluon/impersonation/token', { applicationId: application.id })
            const adminBase = getStoredEnvironment() === GluonEnvironment.PRODUCTION ? 'https://agent.numeo.ai' : 'https://dev-agent.numeo.ai'
            const url = `${adminBase}/auth/sign-in?source=admin-impersonation&token=${encodeURIComponent(data.token)}`
            if (win) win.location.href = url
            else window.open(url, '_blank', 'noopener,noreferrer')
        } catch (error) {
            if (win) win.close()
            console.error('Error logging in as owner:', error)
            setLoginAsError(`Failed to log into ${application.projectName}. See console for details.`)
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        )
    }

    return (
        <React.Fragment>
            <Box
                sx={{
                    display: 'flex',
                    my: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography level="h2" component="h1">
                        Applications ({totalCount})
                    </Typography>
                    {fetching && <CircularProgress size="sm" />}
                    <Button size="sm" variant="soft" color="warning" component="a" href="/applications/multi-mc" startDecorator={<MergeTypeIcon />}>
                        Multi-MC
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="sm" variant="outlined" disabled={page === 1 || fetching} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page === totalPages || fetching} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            <Tabs value={userTypeTab} onChange={(event, value) => setUserTypeTab(value as string)} sx={{ mb: 2 }}>
                <TabList>
                    <Tab value="all">All Users</Tab>
                    <Tab value="numeo">Numeo Users</Tab>
                    <Tab value="extension">Extension Users</Tab>
                </TabList>
            </Tabs>

            <Box className="SearchAndFilters-mobile" sx={{ display: { xs: 'flex', sm: 'none' }, my: 1, gap: 1 }}>
                <Input size="sm" placeholder="Search" startDecorator={<SearchIcon />} sx={{ flexGrow: 1 }} />
                <IconButton size="sm" variant="outlined" color="neutral" onClick={() => setOpen(true)}>
                    <FilterAltIcon />
                </IconButton>
            </Box>

            <Box
                className="SearchAndFilters-tabletUp"
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        '& > *': {
                            minWidth: { xs: '100px', md: '200px' },
                        },
                    }}
                >
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Search by Project Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search applications..."
                            startDecorator={<SearchIcon />}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Search by Email</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by email..."
                            startDecorator={<SearchIcon />}
                            value={emailSearchQuery}
                            onChange={(e) => setEmailSearchQuery(e.target.value)}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Search by Nylas Email</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by Nylas email..."
                            startDecorator={<SearchIcon />}
                            value={nylasEmailSearchQuery}
                            onChange={(e) => setNylasEmailSearchQuery(e.target.value)}
                        />
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Status</FormLabel>
                        <Select
                            size="sm"
                            placeholder="Filter by status"
                            value={statusFilter}
                            onChange={(e, value) => setStatusFilter(value as string)}
                            slotProps={{ button: { sx: { whiteSpace: 'nowrap' } } }}
                        >
                            <Option value="all">All</Option>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                        </Select>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Integration</FormLabel>
                        <Select size="sm" placeholder="All" value={integrationFilter} onChange={(e, value) => setIntegrationFilter(value as string)}>
                            <Option value="all">All</Option>
                            <Option value="other">Other</Option>
                        </Select>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Features</FormLabel>
                        <Select size="sm" placeholder="All Features" value={featureFilter} onChange={(e, value) => setFeatureFilter(value as string)}>
                            <Option value="all">All</Option>
                            {FEATURE_FLAG_OPTIONS.map((feature) => (
                                <Option key={feature} value={feature}>
                                    {getFeatureLabel(feature)}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Sheet
                className="OrderTableContainer"
                variant="outlined"
                sx={{
                    display: { xs: 'none', sm: 'initial' },
                    width: '100%',
                    borderRadius: 'sm',
                    flexShrink: 1,
                    overflow: 'auto',
                    minHeight: 0,
                }}
            >
                {deleteSuccess && (
                    <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                        {deleteSuccess} project has been deleted
                    </Alert>
                )}
                {loginAsError && (
                    <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                        {loginAsError}
                    </Alert>
                )}
                <Table
                    aria-labelledby="tableTitle"
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-paddingY': '4px',
                        '--TableCell-paddingX': '8px',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 200, padding: '12px 6px' }}>Project Name</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Status</th>
                            <th style={{ width: 200, padding: '12px 6px' }}>Owner Email</th>
                            <th style={{ width: 140, padding: '12px 6px' }}>Created at</th>
                            <th style={{ width: 140, padding: '12px 6px' }}>Actions</th>
                            {canDeleteApplication() && <th style={{ width: 50, padding: '12px 6px' }}>Hard Delete</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredApplications.map((row) => (
                            <tr key={row.id} onClick={() => handleRowClick(row)}>
                                <td>
                                    <Tooltip title={row.projectName} placement="top">
                                        <Typography
                                            level="body-xs"
                                            sx={{
                                                maxWidth: '300px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.projectName}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.status}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{getOwnerUser(row)?.email ?? 'No owner'}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.createdAt ? new Date(row.createdAt).toLocaleString() : 'N/A'}</Typography>
                                </td>
                                <td>
                                    <IconButton
                                        size="sm"
                                        variant="plain"
                                        color="primary"
                                        onClick={() => {
                                            handleEditClick(row)
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="sm" variant="plain" color="primary" onClick={() => window.open(`/applications/${row.id}`)}>
                                        <OpenInNew fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="sm"
                                        variant="plain"
                                        color="danger"
                                        onClick={(e) => handleDeleteClick(e, row)}
                                        disabled={row.status === 'inactive'}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                    <Dropdown
                                        onOpenChange={(_, isOpen) => {
                                            if (isOpen) handleFeatureFlagOpen(row)
                                        }}
                                        autoClose={!featureFlagLoading[row.id]}
                                    >
                                        <MenuButton
                                            slots={{ root: IconButton }}
                                            slotProps={{
                                                root: {
                                                    variant: 'plain',
                                                    color: 'primary',
                                                    size: 'sm',
                                                },
                                            }}
                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                        >
                                            <FlagIcon fontSize="small" />
                                        </MenuButton>
                                        <Menu placement="bottom-end" sx={{ minWidth: 180 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                            <MenuItem>
                                                <Typography level="title-sm">Feature Flags</Typography>
                                            </MenuItem>
                                            <ListItem>
                                                <List sx={{ width: '100%' }}>
                                                    {FEATURE_FLAG_OPTIONS.map((feature) => (
                                                        <ListItem key={feature}>
                                                            <Checkbox
                                                                label={getFeatureLabel(feature)}
                                                                checked={isFeatureEnabled(row.id, feature)}
                                                                onChange={() => handleFeatureToggle(row.id, feature)}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </ListItem>
                                            <MenuItem>
                                                <Button
                                                    fullWidth
                                                    onClick={(e) => saveFeatureFlags(e, row)}
                                                    disabled={featureFlagLoading[row.id]}
                                                    color={featureFlagSuccess[row.id] ? 'success' : 'primary'}
                                                >
                                                    {featureFlagLoading[row.id] ? (
                                                        <CircularProgress size="sm" />
                                                    ) : featureFlagSuccess[row.id] ? (
                                                        'Updated!'
                                                    ) : (
                                                        'Apply'
                                                    )}
                                                </Button>
                                            </MenuItem>
                                        </Menu>
                                    </Dropdown>
                                    <Tooltip title="Open in RemoteJS" arrow>
                                        <IconButton variant="plain" color="primary" onClick={(event) => onOpenRemoteJS(event, row.projectName)}>
                                            <OpenInNew fontSize="small" />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Login as owner user button. Available for every active app — the backend
                                        resolves the owner and mints the token, so accounts with no login method
                                        of their own can still be impersonated. */}
                                    {canLoginAsUser() && (
                                        <Tooltip title={`Login as ${getOwnerUser(row)?.email ?? 'owner'}`} arrow>
                                            <IconButton variant="plain" color="success" onClick={(e) => handleLoginAsOwner(e, row)}>
                                                <LoginIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </td>
                                {canDeleteApplication() && (
                                    <td>
                                        <Button variant="plain" color="danger" onClick={(event) => handleHardDeleteClick(event, row.projectName)}>
                                            ...
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>

            <ApplicationDetails
                open={drawerOpen}
                onClose={handleCloseDrawer}
                application={editedData}
                onUpdate={handleUpdate}
                onJsonChange={handleJsonChange}
                updating={updating}
            />

            {/* Delete Confirmation Modal */}
            <Modal open={deleteModalOpen} onClose={handleDeleteCancel}>
                <ModalDialog
                    variant="outlined"
                    role="alertdialog"
                    aria-labelledby="delete-confirmation-title"
                    aria-describedby="delete-confirmation-description"
                >
                    <Typography id="delete-confirmation-title" level="h2">
                        Delete Application
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography id="delete-confirmation-description" textColor="text.tertiary">
                        Are you sure you want to delete the application "{applicationToDelete?.projectName}"? This will disconnect all Nylas integrations and
                        mark the application as inactive.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}>
                        <Button variant="plain" color="neutral" onClick={handleDeleteCancel} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button variant="solid" color="danger" onClick={handleDeleteConfirm} disabled={deleting}>
                            {deleting ? <CircularProgress size="sm" /> : 'Delete'}
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            <Modal open={open} onClose={() => setOpen(false)}>
                <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                    <ModalClose />
                    <Typography id="filter-modal" level="h2">
                        Filters
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Sheet sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5,
                                '& > *': {
                                    minWidth: { xs: '120px', md: '160px' },
                                },
                            }}
                        >
                            <FormControl sx={{ flex: 1 }} size="sm">
                                <FormLabel>Search by Project Name</FormLabel>
                                <Input
                                    size="sm"
                                    placeholder="Search applications..."
                                    startDecorator={<SearchIcon />}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </FormControl>
                            <FormControl sx={{ flex: 1 }} size="sm">
                                <FormLabel>Search by Email</FormLabel>
                                <Input
                                    size="sm"
                                    placeholder="Search by email..."
                                    startDecorator={<SearchIcon />}
                                    value={emailSearchQuery}
                                    onChange={(e) => setEmailSearchQuery(e.target.value)}
                                />
                            </FormControl>
                            <FormControl sx={{ flex: 1 }} size="sm">
                                <FormLabel>Search by Nylas Email</FormLabel>
                                <Input
                                    size="sm"
                                    placeholder="Search by Nylas email..."
                                    startDecorator={<SearchIcon />}
                                    value={nylasEmailSearchQuery}
                                    onChange={(e) => setNylasEmailSearchQuery(e.target.value)}
                                />
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>Status</FormLabel>
                                <Select
                                    size="sm"
                                    placeholder="Filter by status"
                                    value={statusFilter}
                                    onChange={(e, value) => setStatusFilter(value as string)}
                                    slotProps={{ button: { sx: { whiteSpace: 'nowrap' } } }}
                                >
                                    <Option value="all">All</Option>
                                    <Option value="active">Active</Option>
                                    <Option value="inactive">Inactive</Option>
                                </Select>
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>Integration</FormLabel>
                                <Select size="sm" placeholder="All" value={integrationFilter} onChange={(e, value) => setIntegrationFilter(value as string)}>
                                    <Option value="all">All</Option>
                                    <Option value="other">Other</Option>
                                </Select>
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>Features</FormLabel>
                                <Select size="sm" placeholder="All Features" value={featureFilter} onChange={(e, value) => setFeatureFilter(value as string)}>
                                    <Option value="all">All</Option>
                                    {FEATURE_FLAG_OPTIONS.map((feature) => (
                                        <Option key={feature} value={feature}>
                                            {getFeatureLabel(feature)}
                                        </Option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Button color="primary" onClick={() => setOpen(false)}>
                            Submit
                        </Button>
                    </Sheet>
                </ModalDialog>
            </Modal>

            <Modal open={deleteConfirmModalOpen} onClose={() => setDeleteConfirmModalOpen(false)}>
                <ModalDialog>
                    <DialogTitle>
                        <Typography color="danger" level="title-md">
                            Type to Confirm
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography level="body-sm">
                            Please type <b>{deletingProject}</b> to confirm deletion.
                        </Typography>
                        <Input
                            autoFocus
                            placeholder="Type here..."
                            value={deletingProjectInput}
                            onChange={(e) => setDeletingProjectInput(e.target.value)}
                            sx={{ mt: 2 }}
                        />
                        <Typography level="body-sm" color="danger" sx={{ fontStyle: 'italic' }}>
                            This will delete all data associated with this application. This action cannot be undone!!!
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="plain"
                            onClick={() => {
                                setDeleteConfirmModalOpen(false)
                                setDeletingProjectInput('')
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            disabled={deletingProjectInput === '' || deletingProjectInput.toLowerCase() !== deletingProject.toLowerCase()}
                            onClick={handleHardDeleteConfirm}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </React.Fragment>
    )
}
